import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload as UploadIcon, FileSpreadsheet, FileJson, FileText, X } from 'lucide-react';
import { parse as parseCsv } from 'papaparse';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, addDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Business } from '../types';

const Upload = () => {
  const { currentUser } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Business[]>([]);

  const getFileType = (fileName: string) => {
    const ext = fileName.split('.').pop();
    return (ext || '').toLowerCase();
  };

  const parseTextContent = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return [];

    try {
      return JSON.parse(trimmed);
    } catch {
      return await new Promise<any[]>((resolve, reject) => {
        parseCsv(trimmed, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => resolve(results.data),
          error: (error) => reject(error)
        });
      });
    }
  };

  const cleanData = (data: any[]): Business[] => {
    return data
      .filter(item => item.nom && item.adresse && Array.isArray(item.telephones))
      .map(item => ({
        nom: item.nom,
        adresse: item.adresse.replace(/\s*Voir le plan\s*/, '').trim(),
        telephones: item.telephones.filter((tel: string) => 
          tel && !tel.toLowerCase().includes('operations de marketing')
        )
      }));
  };

  const processFile = async (incomingFile: File) => {
    try {
      let data: any[] = [];
      let rawText = '';
      const fileType = getFileType(incomingFile.name);
      
      if (incomingFile.name.endsWith('.json') || incomingFile.name.endsWith('.txt')) {
        rawText = await incomingFile.text();
        data = await parseTextContent(rawText);
      } 
      else if (incomingFile.name.endsWith('.csv')) {
        const text = await incomingFile.text();
        rawText = text;
        const result = await new Promise<any>((resolve) => {
          parseCsv(text, {
            header: true,
            complete: (results) => resolve(results.data)
          });
        });
        data = result;
      } 
      else if (incomingFile.name.match(/\.xlsx?$/)) {
        const buffer = await incomingFile.arrayBuffer();
        const workbook = XLSX.read(buffer);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        data = XLSX.utils.sheet_to_json(worksheet);
        rawText = XLSX.utils.sheet_to_csv(worksheet);
      }

      const cleanedData = cleanData(data);
      setPreview(cleanedData.slice(0, 5));
      return { cleanedData, rawText, fileType };
    } catch (error) {
      console.error('Error processing file:', error);
      const message = error instanceof Error ? error.message : 'Failed to process file';
      throw new Error(message);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const droppedFile = acceptedFiles[0];
    if (droppedFile) {
      try {
        const { cleanedData } = await processFile(droppedFile);
        setFile(droppedFile);
        toast.success('File processed successfully');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Error processing file';
        toast.error(message);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !fileName.trim()) {
      toast.error('Please provide a file name and select a file');
      return;
    }

    setLoading(true);
    try {
      const processed = await processFile(file);
      const recordCount = processed.cleanedData.length || processed.rawText.split(/\r?\n/).filter(Boolean).length;
      
      await addDoc(collection(db, 'uploads'), {
        userId: currentUser?.uid || '',
        fileName: fileName.trim(),
        dateUpload: new Date().toISOString(),
        recordCount,
        businesses: processed.cleanedData,
        rawText: processed.rawText,
        fileType: processed.fileType
      });

      toast.success('Data uploaded successfully');
      setFile(null);
      setFileName('');
      setPreview([]);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white p-8 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Upload Business Data</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nom du fichier</label>
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Ex: Prospects janvier"
                required
              />
            </div>
          </div>

          <div
            {...getRootProps()}
            className={`mt-4 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-500'}`}
          >
            <input {...getInputProps()} />
            <UploadIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              Drag & drop your file here, or click to select
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Supports JSON, CSV, Excel, and TXT files
            </p>
          </div>

          {file && (
            <div className="mt-4 p-4 bg-gray-50 rounded-md flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {file.name.endsWith('.json') ? (
                  <FileJson className="h-6 w-6 text-blue-500" />
                ) : file.name.endsWith('.txt') ? (
                  <FileText className="h-6 w-6 text-purple-500" />
                ) : (
                  <FileSpreadsheet className="h-6 w-6 text-green-500" />
                )}
                <span className="text-sm text-gray-600">{file.name}</span>
              </div>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-gray-400 hover:text-red-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          {preview.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Preview (first 5 entries):</h3>
              <div className="bg-gray-50 p-4 rounded-md overflow-x-auto">
                <pre className="text-xs text-gray-600">
                  {JSON.stringify(preview, null, 2)}
                </pre>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !file}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Uploading...' : 'Upload Data'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Upload;
